
import { computed, defineComponent, onBeforeMount, reactive } from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'
import TheTable from '@/common/components/UI/TheTable/index.vue'
import TableScrollWrapper from '@/common/components/UI/TableScrollWrapper/index.vue'
import Tabs from '@/common/components/UI/Tabs/index.vue'
import NewTable from '@/ui-kit/NewTable/index.vue'

// types
import {
    AdditionalByIdT,
    ShipmentCostDirection,
    StandardParamsByIdT,
    TariffByIdT,
    TariffTableT,
} from '@/core/types/billing.types'
import { TabT } from '@/core/types/components.types'

// realisations
import { billing } from '@/core/realisations/billing.realisation'

// composable
import { useRoute } from 'vue-router'

// constants
import {
    UniqueTariffsOne,
    StandardParamsColumns,
    StandardParamsRows,
    AdditionalServicesColumns,
} from '@/core/constants/columns/IndividualTariffs.columns'
import { INDIVIDUAL_PERCENT_LIST, INDIVIDUAL_PRICE_LIST } from '@/core/constants/billing.constants'
import { AdditionalServicesEnum } from '@/core/enums/billing.enums'

const TABS: TabT[] = [
    { id: 1, name: 'Тариф Перевозки' },
    { id: 2, name: 'Тариф расходов' },
    { id: 3, name: 'Доп. услуги' },
]

const TABLES: TariffTableT[] = [
    { name: 'Зона 1 - Авто', columnNumber: 0, rows: [], shipmentCostDirection: [] },
    { name: 'Зона 2 - Авто', columnNumber: 1, rows: [], shipmentCostDirection: [] },
    { name: 'Зона 2 - Авиа', columnNumber: 2, rows: [], shipmentCostDirection: [] },
    { name: 'Зона 3 - Авто', columnNumber: 3, rows: [], shipmentCostDirection: [] },
    { name: 'Зона 3 - Авиа', columnNumber: 4, rows: [], shipmentCostDirection: [] },
]

export default defineComponent({
    components: {
        'page-title': PageTitle,
        'the-button': TheButton,
        loader: Loader,
        TheTable,
        TableScrollWrapper,
        Tabs,
        NewTable,
    },
    setup() {
        const state = reactive({
            is_loading: false,
            is_failed: false,
            current_tab: 1,
            //
            tariff: null as TariffByIdT[] | null,
            tariff_second: null as StandardParamsByIdT[] | null,
            tariff_third: null as AdditionalByIdT[] | null,
            //
            expanded: [] as number[],
        })

        const route = useRoute()
        const company_id = Number(route.params.id)

        const loadUniqueTariff = async () => {
            try {
                state.is_loading = true
                state.is_failed = false

                const response = await billing.getTariffByCompanyId(company_id)
                state.tariff = response.data

                let index = 0
                let zoneId = 1
                let typeId = 1
                response.data.forEach((tariff) => {
                    if (tariff.zone.id !== zoneId || tariff.type.id !== typeId) {
                        index++
                        zoneId = tariff.zone.id
                        typeId = tariff.type.id
                    }
                    TABLES[index].rows.push(tariff)
                    TABLES[index].shipmentCostDirection.push(tariff.shipmentCostDirection)
                })

                const response_second = await billing.getTariffSecondByCompanyId(company_id)
                state.tariff_second = response_second.data

                const response_third = await billing.getTariffThirdByCompanyId({
                    companyId: company_id,
                    limit: 9999,
                    shipmentTariffId: 2,
                })

                if (response_third) {
                    state.tariff_third = response_third.data.filter(
                        (t) => t.name !== 'manipulator' && t.name !== 'crane'
                    )
                }
            } catch (error) {
                console.log(error)
                state.is_failed = true
            } finally {
                state.is_loading = false
                state.is_failed = false
            }
        }

        const getSign = (name: string) => {
            if (INDIVIDUAL_PERCENT_LIST.includes(name)) return '(%)'
            else if (INDIVIDUAL_PRICE_LIST.includes(name)) return '(₸)'
            else if (name === 'overloadWeight') return '(кг)'
            else return ''
        }

        const getParamsIndex = (key: string) => {
            let key_arr: string[] = key.split('-')

            let key_right = Number(key_arr[1])
            let key_left = Number(key_arr[0])

            let index = key_left + (key_right - 1) * 13

            return index < 13 ? index * 2 : index - (26 - index) + 1
        }

        const onTabClick = (tabId: number) => (state.current_tab = tabId)

        const getWeightTitle = (tariff: TariffByIdT, tariffs: TariffByIdT[]) => {
            if (!state.tariff) return '-'

            const weights = tariffs.map((tariff) => tariff.weight).reverse()

            const weight = tariff.weight
            const weightBefore = weights.find((w) => w !== null)

            return weight ? `до ${weight} кг` : `свыше ${weightBefore} кг`
        }

        const toggleRow = (row: TariffByIdT) => {
            if (state.expanded.includes(row.id)) {
                state.expanded.splice(state.expanded.indexOf(row.id, 1))
            } else {
                state.expanded.push(row.id)
            }
        }

        const isExpanded = (id: number) => state.expanded.includes(id)

        const getDirection = (row: ShipmentCostDirection) => {
            const { fromCity, whereCity } = row

            const result = `${fromCity ? fromCity.name : '-'} - ${whereCity ? whereCity.name : '-'}`
            const resultArr: string[] = []

            if (row.isSelfDelivery) resultArr.push('самопривоз')
            if (row.isPickUp) resultArr.push('самовывоз')
            const resultAdditional = resultArr.length ? `(${resultArr.join(', ')})` : ''

            return `${result} ${resultAdditional}`
        }

        const getCompanyName = computed(() => {
            if (!state.tariff || !state.tariff[0]) return ''

            let result = `(${state.tariff[0].companyName})`
            if (state.tariff[0].vatEnabled) result += ' - НДС включен'
            else result += ' - НДС выключен'
            return result
        })

        const isVatEnabled = computed(
            () => state.tariff && state.tariff[0] && state.tariff[0].vatEnabled
        )
        const isVatDisabled = computed(
            () => state.tariff && state.tariff[0] && !state.tariff[0].vatEnabled
        )
        const tablesSecond = computed(() => TABLES.slice(1))

        onBeforeMount(async () => {
            await loadUniqueTariff()
        })

        return {
            state,
            company_id,
            loadUniqueTariff,
            getParamsIndex,
            getSign,
            onTabClick,
            getWeightTitle,
            getDirection,
            //
            UniqueTariffsOne,
            StandardParamsColumns,
            AdditionalServicesColumns,
            StandardParamsRows,
            AdditionalServicesEnum,
            tablesSecond,
            toggleRow,
            isExpanded,
            //
            getCompanyName,
            isVatEnabled,
            isVatDisabled,
            //
            TABS,
            TABLES,
        }
    },
})
